import { Button } from "components/DesignSystem/Button/Button";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { SettingRow } from "pages/Books/Settings/BooksSettings";
import { useEffect, useRef } from "react";
import { emptyApi } from "store/apis/emptyApi";
import {
  useGetFinallyImportQuery,
  useRevertFinallyImportMutation,
} from "store/apis/finallyImport";
import { BOOKS_IMPORT_STATUS } from "types/Models/books";
import { BackendError } from "types/utils/error";
import {
  FINALLY_IMPORT_POLLING_INTERVAL,
  FINALLY_REVERTED_DATA_TAGS,
  FINALLY_REVERT_FAILED_MESSAGE,
  FINALLY_REVERT_POLLING_INTERVAL,
} from "./constants";
import { FinallyImportActionsMenu } from "./FinallyImportActionsMenu";
import { FinallyImportView } from "./FinallyImportView";
import { ImportFinally } from "./ImportFinally";
import { RevertFinallyImportModal } from "./RevertFinallyImportModal";

type FinallyImportSettingRowProps = {
  entityId: string;
};

export const FinallyImportSettingRow = ({
  entityId,
}: FinallyImportSettingRowProps) => {
  const importModal = useModal();
  const viewModal = useModal();
  const revertModal = useModal();
  const { success, error } = useToast();
  const dispatch = useAppDispatch();

  const [revertFinallyImport, { isLoading: isRevertRequestLoading }] =
    useRevertFinallyImportMutation();

  const { data } = useGetFinallyImportQuery({ entityId }, { skip: !entityId });

  const status = data?.status;
  const isInProgress = status === BOOKS_IMPORT_STATUS.IN_PROGRESS;
  const isReverting = status === BOOKS_IMPORT_STATUS.REVERTING;
  const isCompleted = status === BOOKS_IMPORT_STATUS.COMPLETED;
  const resumeImportProcessId = data?.coa_import_state_id ?? null;
  const hasUploadedFiles = Boolean(data?.coa_file || data?.je_file);
  const canRevert =
    isCompleted ||
    (status === BOOKS_IMPORT_STATUS.NOT_STARTED && hasUploadedFiles);

  const previousStatusRef = useRef(status);

  let pollingInterval = FINALLY_IMPORT_POLLING_INTERVAL;

  if (isReverting) {
    pollingInterval = FINALLY_REVERT_POLLING_INTERVAL;
  }

  useGetFinallyImportQuery(
    { entityId },
    {
      skip: !entityId || !(isInProgress || isReverting),
      pollingInterval,
    }
  );

  useEffect(() => {
    const wasReverting =
      previousStatusRef.current === BOOKS_IMPORT_STATUS.REVERTING;
    previousStatusRef.current = status;

    if (!wasReverting || !status || status === BOOKS_IMPORT_STATUS.REVERTING) {
      return;
    }

    const isRevertDone =
      status === BOOKS_IMPORT_STATUS.NOT_STARTED && !hasUploadedFiles;

    if (!isRevertDone) {
      error({ message: FINALLY_REVERT_FAILED_MESSAGE });
      return;
    }

    dispatch(emptyApi.util.invalidateTags([...FINALLY_REVERTED_DATA_TAGS]));
    success({ message: "Finally migration reverted" });
  }, [status, hasUploadedFiles, dispatch, error, success]);

  let buttonText = "Import";
  let isDisabled = !status;
  let handleClick = importModal.open;

  if (status === BOOKS_IMPORT_STATUS.NOT_STARTED && resumeImportProcessId) {
    buttonText = "Resume";
  }

  if (isInProgress) {
    buttonText = "In progress";
    isDisabled = true;
  }

  if (isReverting) {
    buttonText = "Reverting…";
    isDisabled = true;
  }

  if (isCompleted) {
    buttonText = "View";
    handleClick = viewModal.open;
  }

  const handleRevert = async () => {
    try {
      await revertFinallyImport({ entityId }).unwrap();
      revertModal.close();
    } catch (err) {
      error(
        {
          message:
            (err as BackendError)?.data?.error?.message ??
            "Something went wrong",
        },
        err as Error
      );
    }
  };

  return (
    <>
      <SettingRow
        title="Import data from Finally"
        description="Import the chart of accounts and journal entries from Finally to resume bookkeeping within Inkle books. Visible to admins only."
      >
        <div className="t-flex t-gap-2 t-items-center">
          <Button
            customType="secondary"
            size="small"
            onClick={handleClick}
            disabled={isDisabled}
          >
            {buttonText}
          </Button>
          {canRevert && (
            <FinallyImportActionsMenu onRevert={revertModal.open} />
          )}
        </div>
      </SettingRow>

      {importModal.isOpen && (
        <ImportFinally
          {...importModal}
          entityId={entityId}
          resumeImportProcessId={resumeImportProcessId}
        />
      )}
      <FinallyImportView
        entityId={entityId}
        isOpen={viewModal.isOpen}
        onClose={viewModal.close}
      />
      <RevertFinallyImportModal
        open={revertModal.isOpen}
        hasImportRun={isCompleted}
        prevFyEndDate={data?.prev_fy_end_date ?? null}
        dateTillImport={data?.date_till_import ?? null}
        isLoading={isRevertRequestLoading}
        onCancel={revertModal.close}
        onConfirm={handleRevert}
      />
    </>
  );
};
